import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMachines } from '../context/MachineContext';
import { uploadMachineImage } from '../lib/appBackend';
import { getCategoryIcon, validateImageFile } from '../utils';
import styles from './AdminPage.module.css';

export default function AdminPage() {
  const {
    machines, addMachine, updateMachine, deleteMachine, logout, addToast,
    categories, addCategory, updateCategory, deleteCategory, changePassword,
  } = useMachines();
  const navigate = useNavigate();

  const [tab, setTab] = useState('list');       // 'list' | 'add' | 'edit' | 'categories' | 'password'
  const [editId, setEditId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [filterCat, setFilterCat] = useState('');
  const [search, setSearch] = useState('');

  // ── CHANGE PASSWORD ────────────────────────────────────────────────────────
  const [pwdForm, setPwdForm] = useState({ current: '', newPwd: '', confirm: '' });
  const [pwdError, setPwdError] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdError('');
    if (pwdForm.newPwd !== pwdForm.confirm) {
      setPwdError('New passwords do not match.');
      return;
    }
    setPwdLoading(true);
    const { ok, error } = await changePassword(pwdForm.current, pwdForm.newPwd);
    setPwdLoading(false);
    if (ok) {
      addToast('Password changed successfully.', 'success');
      setPwdForm({ current: '', newPwd: '', confirm: '' });
      setTab('list');
    } else {
      setPwdError(error);
    }
  };

  // ── FILTERED LIST ─────────────────────────────────────────────────────────
  const filtered = machines.filter(m => {
    if (filterCat && m.category !== filterCat) return false;
    if (search && !m.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const openEdit   = (id) => { setEditId(id); setTab('edit'); };
  const openAdd    = ()   => { setEditId(null); setTab('add'); };
  const backToList = ()   => { setTab('list'); setEditId(null); };

  const handleDelete = (id) => {
    deleteMachine(id);
    setDeleteConfirm(null);
    addToast('Machine deleted successfully.', 'success');
  };

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className={`${styles.page} page-wrapper`}>
      <div className="container">
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.pageTitle}>Admin Dashboard</h1>
            <p className={styles.pageSub}>Manage all machinery listings for Bright Machineries</p>
          </div>
          <div className={styles.headerActions}>
            {tab !== 'list' && (
              <button className={styles.backBtn} onClick={backToList}>← Back to List</button>
            )}
            {tab === 'list' && (
              <>
                <button className={styles.catMgrBtn} onClick={() => setTab('categories')}>🏷️ Categories</button>
                <button className={styles.catMgrBtn} onClick={openAdd}>+ Add New Machine</button>
                <button className={styles.catMgrBtn} onClick={() => { setPwdForm({ current: '', newPwd: '', confirm: '' }); setPwdError(''); setTab('password'); }}>🔑 Change Password</button>
              </>
            )}
            <button className={styles.logoutBtn} onClick={handleLogout}>Sign Out</button>
          </div>
        </div>

        {/* Stats */}
        <div className={styles.statsRow}>
          {[
            { label: 'Total',      value: machines.length,                color: 'var(--text-primary)' },
            { label: 'Available',  value: machines.filter(m=>m.status==='Available').length, color: 'var(--green)' },
            { label: 'Reserved',   value: machines.filter(m=>m.status==='Reserved').length,  color: 'var(--yellow)' },
            { label: 'Sold',       value: machines.filter(m=>m.status==='Sold').length,      color: 'var(--red)' },
          ].map(s => (
            <div key={s.label} className={styles.statCard}>
              <div className={styles.statVal} style={{ color: s.color }}>{s.value}</div>
              <div className={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── LIST TAB ──────────────────────────────────────────────────────── */}
        {tab === 'list' && (
          <>
            {/* Filters */}
            <div className={styles.filterRow}>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search machines…"
                className={styles.searchInput}
              />
              <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className={styles.filterSelect}>
                <option value="">All Categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>

            {/* Table */}
            <div className={styles.tableWrap}>
              {filtered.length === 0 ? (
                <div className={styles.emptyState}>
                  <div style={{fontSize:40,marginBottom:12}}>📦</div>
                  <p>No machines found. <button className={styles.linkBtn} onClick={openAdd}>Add the first one →</button></p>
                </div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Machine</th>
                      <th>Category</th>
                      <th>Location</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(m => (
                      <tr key={m.id}>
                        <td>
                          <div className={styles.cellTitle}>
                            <div className={styles.thumbCell}>
                              {m.images?.length > 0
                                ? <img src={m.images[0]} alt="" className={styles.rowThumb} />
                                : <span className={styles.rowThumbIcon}>{getCategoryIcon(m.category, categories)}</span>
                              }
                            </div>
                            <div>
                              <div className={styles.machineName}>{m.title}</div>
                              <div className={styles.machineBrand}>{m.brand} · {m.year}</div>
                            </div>
                          </div>
                        </td>
                        <td className={styles.tdMuted}>{categories.find(c=>c.id===m.category)?.label || m.category}</td>
                        <td className={styles.tdMuted}>{m.location}</td>
                        <td>
                          <div className={styles.actionBtns}>
                            <button className={styles.editBtn} onClick={() => openEdit(m.id)}>Edit</button>
                            <button
                              className={styles.deleteBtn}
                              onClick={() => setDeleteConfirm(m.id)}
                            >Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* ── CHANGE PASSWORD TAB ──────────────────────────────────────────── */}
        {tab === 'password' && (
          <div className={styles.pwdPanel}>
            <h2 className={styles.sectionTitle}>🔑 Change Password</h2>
            <p className={styles.sectionSub}>Password must be 1–12 alphanumeric characters (letters and numbers only).</p>
            <form className={styles.pwdForm} onSubmit={handleChangePassword}>
              <div className={styles.pwdField}>
                <label className={styles.label}>Current Password</label>
                <input
                  type="password"
                  value={pwdForm.current}
                  onChange={e => setPwdForm(p => ({ ...p, current: e.target.value }))}
                  className={styles.pwdInput}
                  placeholder="Enter current password"
                  maxLength={12}
                  autoComplete="current-password"
                  autoFocus
                />
              </div>
              <div className={styles.pwdField}>
                <label className={styles.label}>New Password</label>
                <input
                  type="password"
                  value={pwdForm.newPwd}
                  onChange={e => setPwdForm(p => ({ ...p, newPwd: e.target.value }))}
                  className={styles.pwdInput}
                  placeholder="1–12 alphanumeric characters"
                  maxLength={12}
                  autoComplete="new-password"
                />
                <span className={styles.pwdHint}>{pwdForm.newPwd.length}/12 characters</span>
              </div>
              <div className={styles.pwdField}>
                <label className={styles.label}>Confirm New Password</label>
                <input
                  type="password"
                  value={pwdForm.confirm}
                  onChange={e => setPwdForm(p => ({ ...p, confirm: e.target.value }))}
                  className={styles.pwdInput}
                  placeholder="Repeat new password"
                  maxLength={12}
                  autoComplete="new-password"
                />
              </div>
              {pwdError && <p className={styles.pwdError}>{pwdError}</p>}
              <div className={styles.pwdActions}>
                <button
                  type="submit"
                  className={styles.saveBtn}
                  disabled={pwdLoading || !pwdForm.current || !pwdForm.newPwd || !pwdForm.confirm}
                >
                  {pwdLoading ? 'Saving…' : '🔑 Update Password'}
                </button>
                <button type="button" className={styles.cancelFormBtn} onClick={backToList}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* ── CATEGORIES TAB ────────────────────────────────────────────────── */}
        {tab === 'categories' && (
          <CategoriesManager
            categories={categories}
            onAdd={addCategory}
            onUpdate={updateCategory}
            onDelete={deleteCategory}
            addToast={addToast}
            onBack={backToList}
          />
        )}

        {/* ── ADD / EDIT TAB ────────────────────────────────────────────────── */}
        {(tab === 'add' || tab === 'edit') && (
          <MachineForm
            machine={tab === 'edit' ? machines.find(m => m.id === editId) : null}
            categories={categories}
            onSave={async (data) => {
              if (tab === 'edit') {
                await updateMachine(editId, data);
                addToast('Machine updated successfully!', 'success');
              } else {
                await addMachine(data);
                addToast('Machine added successfully!', 'success');
              }
              backToList();
            }}
            onCancel={backToList}
          />
        )}
      </div>

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className={styles.modalOverlay} onClick={() => setDeleteConfirm(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalIcon}>🗑️</div>
            <h3 className={styles.modalTitle}>Delete Machine?</h3>
            <p className={styles.modalSub}>This action cannot be undone. The listing will be permanently removed.</p>
            <div className={styles.modalActions}>
              <button className={styles.confirmDeleteBtn} onClick={() => handleDelete(deleteConfirm)}>
                Yes, Delete
              </button>
              <button className={styles.cancelBtn} onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CATEGORIES MANAGER ───────────────────────────────────────────────────────
function CategoriesManager({ categories, onAdd, onUpdate, onDelete, addToast, onBack }) {
  const [newLabel, setNewLabel] = useState('');
  const [newIcon,  setNewIcon]  = useState('📦');
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editIcon,  setEditIcon]  = useState('');
  const [deleteCatConfirm, setDeleteCatConfirm] = useState(null);

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;
    onAdd({ label, icon: newIcon });
    setNewLabel('');
    setNewIcon('📦');
    addToast('Category added.', 'success');
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditLabel(cat.label);
    setEditIcon(cat.icon);
  };

  const saveEdit = () => {
    if (!editLabel.trim()) return;
    onUpdate(editingId, { label: editLabel.trim(), icon: editIcon });
    setEditingId(null);
    addToast('Category updated.', 'success');
  };

  const handleDelete = (id) => {
    onDelete(id);
    setDeleteCatConfirm(null);
    addToast('Category deleted.', 'success');
  };

  return (
    <div className={styles.catManager}>
      <div className={styles.catManagerHeader}>
        <h2 className={styles.formTitle}>Manage Categories</h2>
      </div>

      {/* Add new */}
      <div className={styles.catAddRow}>
        <input
          type="text"
          value={newIcon}
          onChange={e => setNewIcon(e.target.value)}
          placeholder="Icon"
          className={styles.iconInput}
          maxLength={4}
        />
        <input
          type="text"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          placeholder="New category name…"
          className={styles.input}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
        />
        <button
          className={styles.addBtn}
          onClick={handleAdd}
          disabled={!newLabel.trim()}
        >+ Add</button>
      </div>

      {/* List */}
      <div className={styles.catList}>
        {categories.map(cat => (
          <div key={cat.id} className={styles.catRow}>
            {editingId === cat.id ? (
              <>
                <input
                  type="text"
                  value={editIcon}
                  onChange={e => setEditIcon(e.target.value)}
                  className={styles.iconInput}
                  maxLength={4}
                />
                <input
                  type="text"
                  value={editLabel}
                  onChange={e => setEditLabel(e.target.value)}
                  className={`${styles.input} ${styles.catEditInput}`}
                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingId(null); }}
                  autoFocus
                />
                <button className={styles.editBtn} onClick={saveEdit}>Save</button>
                <button className={styles.cancelBtn} onClick={() => setEditingId(null)}>Cancel</button>
              </>
            ) : (
              <>
                <span className={styles.catIcon}>{cat.icon}</span>
                <span className={styles.catLabel}>{cat.label}</span>
                <span className={styles.catId}>({cat.id})</span>
                <div className={styles.actionBtns}>
                  <button className={styles.editBtn} onClick={() => startEdit(cat)}>Edit</button>
                  <button className={styles.deleteBtn} onClick={() => setDeleteCatConfirm(cat.id)}>Delete</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Delete confirm */}
      {deleteCatConfirm && (
        <div className={styles.modalOverlay} onClick={() => setDeleteCatConfirm(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalIcon}>🏷️</div>
            <h3 className={styles.modalTitle}>Delete Category?</h3>
            <p className={styles.modalSub}>Machines in this category will keep their category ID but it won't appear in filters.</p>
            <div className={styles.modalActions}>
              <button className={styles.confirmDeleteBtn} onClick={() => handleDelete(deleteCatConfirm)}>Yes, Delete</button>
              <button className={styles.cancelBtn} onClick={() => setDeleteCatConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MACHINE FORM COMPONENT ────────────────────────────────────────────────────
function MachineForm({ machine, categories, onSave, onCancel }) {
  const [form, setForm] = useState({
    title:       machine?.title       || '',
    brand:       machine?.brand       || '',
    category:    machine?.category    || '',
    year:        machine?.year        || new Date().getFullYear().toString(),
    location:    machine?.location    || '',
    description: machine?.description || '',
    specs:       machine?.specs       || '',
    featured:    machine?.featured    || false,
    images:      machine?.images      || [],
  });
  const [errors, setErrors]     = useState({});
  const [uploading, setUploading] = useState(false);

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  const validate = () => {
    const e = {};
    if (!form.title.trim())    e.title    = 'Title is required.';
    if (!form.brand.trim())    e.brand    = 'Brand is required.';
    if (!form.category)        e.category = 'Category is required.';
    if (!form.location.trim()) e.location = 'Location is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setUploading(true);
    try {
      await onSave({ ...form });
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const results = [];
      for (const file of files) {
        const err = validateImageFile(file);
        if (err) { alert(err); continue; }
        results.push(await uploadMachineImage(file));
      }
      if (results.length) setForm(f => ({ ...f, images: [...f.images, ...results] }));
    } catch (error) {
      alert(error.message || 'Image upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const moveImage = (from, to) => {
    setForm(f => {
      const imgs = [...f.images];
      const [moved] = imgs.splice(from, 1);
      imgs.splice(to, 0, moved);
      return { ...f, images: imgs };
    });
  };

  return (
    <div className={styles.formWrap}>
      <h2 className={styles.formTitle}>{machine ? 'Edit Machine' : 'Add New Machine'}</h2>

      <div className={styles.formGrid}>
        {/* Title */}
        <div className={`${styles.field} ${styles.fullWidth}`}>
          <label className={styles.label}>Machine Title <span>*</span></label>
          <input
            type="text"
            value={form.title}
            onChange={e => set('title')(e.target.value)}
            placeholder="e.g. Fanuc Robodrill D21MiB5 CNC Machining Centre"
            className={`${styles.input} ${errors.title ? styles.inputError : ''}`}
          />
          {errors.title && <span className={styles.error}>{errors.title}</span>}
        </div>

        {/* Brand */}
        <div className={styles.field}>
          <label className={styles.label}>Brand / Make <span>*</span></label>
          <input
            type="text"
            value={form.brand}
            onChange={e => set('brand')(e.target.value)}
            placeholder="e.g. Fanuc, Komatsu, Kirloskar"
            className={`${styles.input} ${errors.brand ? styles.inputError : ''}`}
          />
          {errors.brand && <span className={styles.error}>{errors.brand}</span>}
        </div>

        {/* Category */}
        <div className={styles.field}>
          <label className={styles.label}>Category <span>*</span></label>
          <select
            value={form.category}
            onChange={e => set('category')(e.target.value)}
            className={`${styles.select} ${errors.category ? styles.inputError : ''}`}
          >
            <option value="">Select category…</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
          </select>
          {errors.category && <span className={styles.error}>{errors.category}</span>}
        </div>

        {/* Year */}
        <div className={styles.field}>
          <label className={styles.label}>Year of Manufacture</label>
          <input
            type="text"
            value={form.year}
            onChange={e => set('year')(e.target.value)}
            placeholder="e.g. 2019"
            className={styles.input}
          />
        </div>

        {/* Location */}
        <div className={styles.field}>
          <label className={styles.label}>Location <span>*</span></label>
          <input
            type="text"
            value={form.location}
            onChange={e => set('location')(e.target.value)}
            placeholder="e.g. Chennai, TN"
            className={`${styles.input} ${errors.location ? styles.inputError : ''}`}
          />
          {errors.location && <span className={styles.error}>{errors.location}</span>}
        </div>

        {/* Specs */}
        <div className={`${styles.field} ${styles.fullWidth}`}>
          <label className={styles.label}>Key Specifications <span className={styles.optional}>(one-liner)</span></label>
          <input
            type="text"
            value={form.specs}
            onChange={e => set('specs')(e.target.value)}
            placeholder="e.g. 20,000 RPM | BT30 | 5-Axis | X/Y/Z: 300/300/250 mm"
            className={styles.input}
          />
          <span className={styles.hint}>Short spec summary shown on the listing card</span>
        </div>

        {/* Description */}
        <div className={`${styles.field} ${styles.fullWidth}`}>
          <label className={styles.label}>Detailed Description</label>
          <textarea
            value={form.description}
            onChange={e => set('description')(e.target.value)}
            placeholder="Full description: condition notes, history, accessories included, reason for sale, contact for inspection…"
            className={styles.textarea}
            rows={5}
          />
        </div>

        {/* Featured toggle */}
        <div className={`${styles.field} ${styles.fullWidth}`}>
          <label className={styles.checkLabel}>
            <input
              type="checkbox"
              checked={form.featured}
              onChange={e => set('featured')(e.target.checked)}
              className={styles.checkbox}
            />
            <span>Feature this machine on the Home page</span>
          </label>
        </div>

        {/* Images */}
        <div className={`${styles.field} ${styles.fullWidth}`}>
          <label className={styles.label}>Photos</label>
          <div className={styles.uploadArea}>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={handleImageUpload}
              className={styles.fileInput}
              id="imgUpload"
              disabled={uploading}
            />
            <label htmlFor="imgUpload" className={styles.uploadLabel}>
              {uploading ? (
                <span>Uploading…</span>
              ) : (
                <>
                  <span className={styles.uploadIcon}>📷</span>
                  <span className={styles.uploadText}>Click to upload photos</span>
                  <span className={styles.uploadHint}>JPG, PNG, WebP · Max 5 MB per image · Multiple allowed</span>
                </>
              )}
            </label>
          </div>

          {form.images.length > 0 && (
            <div className={styles.imagePreviewGrid}>
              {form.images.map((img, i) => (
                <div key={i} className={styles.imgPreview}>
                  <img src={img} alt={`Preview ${i + 1}`} className={styles.previewImg} />
                  {i === 0 && <div className={styles.primaryTag}>Primary</div>}
                  <div className={styles.imgActions}>
                    {i > 0 && (
                      <button className={styles.imgMoveBtn} onClick={() => moveImage(i, i - 1)} title="Move left">←</button>
                    )}
                    <button className={styles.imgRemoveBtn} onClick={() => removeImage(i)} title="Remove">×</button>
                    {i < form.images.length - 1 && (
                      <button className={styles.imgMoveBtn} onClick={() => moveImage(i, i + 1)} title="Move right">→</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <span className={styles.hint}>First image is the primary thumbnail. Drag or use arrows to reorder.</span>
        </div>
      </div>

      {/* Form actions */}
      <div className={styles.formActions}>
        <button className={styles.saveBtn} onClick={handleSubmit} disabled={uploading}>
          {machine ? '💾 Save Changes' : '✅ Add Machine'}
        </button>
        <button className={styles.cancelFormBtn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}